// 製作（全站製作、專案的製作）在四個畫面之間切換：進度、後台、動畫師、導演（純邏輯，畫面在 components/WorkspaceSwitcher.tsx）
//
//   進度   /preview/<專案>                      審片頁的總覽（沒選歌），拖卡片改進度
//   後台   /admin/projects/<專案>               專案編輯頁
//   動畫師 /studio/<專案>                       動畫師工作站（用自己的身分）
//   導演   /preview/<專案>?as=director          用導演的眼睛看的唯讀預覽（不會存）
//
// 規則：
//   - 只有帳號是全站製作、或「這個專案的製作」才看得到；動畫師、導演、視覺統籌完全沒有這一排
//   - 歌跟著走：切換時網址帶 ?song=<id>（Preview、Studio 本來就吃這個參數，後台頁新增）；
//     進度格回到沒選歌的總覽，但記得上一首（記在這個分頁的 sessionStorage），切到別格還是那一首
//   - 切換帶 sw=1：頁面收到後不用說「那首歌已經不在了」之類的提醒（歌被刪了就安靜地選第一首）

import type { ProjectRole } from './mockData';

export type WorkspaceKey = 'progress' | 'backend' | 'animator' | 'director';

export const WORKSPACE_ORDER: readonly WorkspaceKey[] = ['progress', 'backend', 'animator', 'director'];

export const WORKSPACE_LABEL: Record<WorkspaceKey, string> = {
  progress: '進度',
  backend: '後台',
  animator: '動畫師',
  director: '導演',
};

/** 滑鼠提示（常駐的字只留名字） */
export const WORKSPACE_HINT: Record<WorkspaceKey, string> = {
  progress: '進度：全部歌的狀態，拖卡片改進度',
  backend: '後台：編輯這個專案',
  animator: '動畫師工作站：用自己的身分操作',
  director: '導演畫面：用導演的眼睛看（預覽，不會存）',
};

/** 每個畫面一個顏色（全站 CSS 變數 --ws-* 也是這四個，global.css） */
export const WORKSPACE_COLOR: Record<WorkspaceKey, string> = {
  progress: '#5aa9ff',
  backend: '#c9ced6',
  animator: '#10c78a',
  director: '#a78bfa',
};

/** 快速鍵：Alt+1～4（跟 WORKSPACE_ORDER 一樣的順序） */
export function workspaceHotkey(key: WorkspaceKey): string {
  return `Alt+${WORKSPACE_ORDER.indexOf(key) + 1}`;
}

/** 誰看得到切換器：帳號是全站製作，或這個專案裡的角色是製作（不是看起來的角色，是真實的） */
export function canSwitchWorkspaces(accountRole: string | null | undefined, realProjectRole: ProjectRole | string | null | undefined): boolean {
  return accountRole === 'admin' || realProjectRole === 'admin';
}

const ID_RE = /^[a-zA-Z0-9_-]{1,64}$/;

/** 切過去的網址。songId = 要帶過去的歌（進度格不帶：回總覽） */
export function workspaceHref(key: WorkspaceKey, projectId: string, songId?: string | null): string {
  const pid = encodeURIComponent(projectId);
  const song = songId && ID_RE.test(songId) ? songId : null;
  const q = new URLSearchParams();
  switch (key) {
    case 'progress':
      return `/preview/${pid}`;
    case 'director':
      q.set('as', 'director');
      break;
    default:
      break;
  }
  if (song) { q.set('song', song); q.set('sw', '1'); }
  const qs = q.toString();
  const base = key === 'backend' ? `/admin/projects/${pid}` : key === 'animator' ? `/studio/${pid}` : `/preview/${pid}`;
  return qs ? `${base}?${qs}` : base;
}

/** 這個網址是不是切換器帶過來的（sw=1）：歌找不到時不用跳提醒 */
export function isSwitchLink(search: string | URLSearchParams): boolean {
  const q = typeof search === 'string' ? new URLSearchParams(search) : search;
  return q.get('sw') === '1';
}

/** 在 Preview / 預覽裡，現在算哪一個畫面 */
export function previewWorkspace(viewAsRole: string | null | undefined): WorkspaceKey {
  return viewAsRole === 'director' ? 'director' : 'progress';
}

// ─── 記住上一首歌（這個分頁） ───

const SONG_KEY = (projectId: string) => `sp_ws_song:${projectId}`;

export function recallSong(projectId: string, storage: Pick<Storage, 'getItem'> | null = safeSession()): string | null {
  try {
    const v = storage?.getItem(SONG_KEY(projectId)) ?? null;
    return v && ID_RE.test(v) ? v : null;
  } catch { return null; }
}

export function rememberSong(projectId: string, songId: string | null | undefined, storage: Pick<Storage, 'setItem'> | null = safeSession()): void {
  if (!songId || !ID_RE.test(songId)) return;
  try { storage?.setItem(SONG_KEY(projectId), songId); } catch { /* 無痕模式 → 不記 */ }
}

function safeSession(): Storage | null {
  try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; }
}

// ─── 快速鍵 ───

/** 在輸入框、文字欄、下拉選單、可編輯區打字時，不要當成快速鍵 */
export function isTypingTarget(el: { tagName?: string; isContentEditable?: boolean } | null | undefined): boolean {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = (el.tagName || '').toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Alt+1～4 → 哪一個畫面（Mac 上 Alt+數字會變成符號，所以看實體鍵 code）；不是就 null */
export function hotkeyWorkspace(e: { altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; code?: string; key?: string }): WorkspaceKey | null {
  if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return null;
  const m = /^(?:Digit|Numpad)([1-4])$/.exec(e.code || '') ?? /^([1-4])$/.exec(e.key || '');
  return m ? WORKSPACE_ORDER[Number(m[1]) - 1] : null;
}

/** 點的時候該不該交給瀏覽器（Ctrl / Cmd / Shift 點、滑鼠中鍵 = 開新分頁 / 新視窗） */
export function opensInNewTab(e: { button?: number; ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }): boolean {
  return (e.button ?? 0) !== 0 || !!e.ctrlKey || !!e.metaKey || !!e.shiftKey;
}

// ─── 進度版拖卡片 ───

export type BoardStatus = 'todo' | 'in_review' | 'needs_changes' | 'approved';
export const BOARD_ORDER: readonly BoardStatus[] = ['todo', 'in_review', 'needs_changes', 'approved'];

/**
 * 誰能在進度版拖卡片：帳號是全站製作或專案角色是製作；導演、視覺統籌不行。
 * 預覽只有 PM 畫面能試拖（畫面動、不會存，Preview 的 moveSong）；其他預覽不行。
 * realProjectRole = 真實的專案角色（不是預覽換過的）
 */
export function canDragBoard(p: { accountRole: string | null | undefined; realProjectRole: string | null | undefined; viewAsActive: boolean; viewAsRole?: string | null }): boolean {
  if (p.viewAsActive && p.viewAsRole !== 'pm') return false;
  return p.accountRole === 'admin' || p.realProjectRole === 'admin';
}

/** 鍵盤 Alt+左 / 右：往旁邊一欄；到頭了 null */
export function neighborStatus(from: BoardStatus, dir: -1 | 1): BoardStatus | null {
  const i = BOARD_ORDER.indexOf(from);
  const j = i + dir;
  return i < 0 || j < 0 || j >= BOARD_ORDER.length ? null : BOARD_ORDER[j];
}

/** 卡片上的「移到…」選單（拖不動的時候用：手機、不支援拖曳的瀏覽器）：其他三欄，照欄的順序 */
export function boardMoveTargets(from: BoardStatus): BoardStatus[] {
  return BOARD_ORDER.filter(s => s !== from);
}

/** 放開之後要不要真的改：拖回原本那一欄 = 不用 */
export function dropChangesStatus(from: BoardStatus, to: BoardStatus): boolean {
  return from !== to;
}
